import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { AwardSvg } from '../../../assets/svg/award';
import { getRupeeWithDecimal } from '../../../utils/numbers';
import { getHistoricalMetalData } from '../useDealDetails';
import { baseColors } from '../../../assets/theme';
import { LineChart, areaElementClasses, useDrawingArea } from '@mui/x-charts';
import dayjs from 'dayjs';

import utc from 'dayjs/plugin/utc';
import { TimerComponent } from './components/TimerComponent';
import { TransactionTile } from './components/TransactionTile';
import LoadingDots from '../../../components/molecules/LoadingDotsProps';
import { ArrowDownwardRounded, ArrowUpwardRounded } from '@mui/icons-material';
import silverBannerImage from '../../../assets/images/silverBanner.webp';
dayjs.extend(utc);

const getDateRange = (view: string) => {
  const today = dayjs(); // Use Day.js to handle dates more easily
  let fromDate: dayjs.Dayjs;

  switch (view) {
    case 'ONE_WEEK':
      fromDate = today.subtract(1, 'week');
      break;
    case 'ONE_MONTH':
      fromDate = today.subtract(1, 'month');
      break;
    case 'SIX_MONTHS':
      fromDate = today.subtract(6, 'month');
      break;
    case 'ONE_YEAR':
      fromDate = today.subtract(1, 'year');
      break;
    case 'FIVE_YEARS':
      fromDate = today.subtract(5, 'year');
      break;
    default:
      fromDate = today.subtract(1, 'week'); // Default to one week if no match
      break;
  }

  return {
    from: fromDate.format('YYYY-MM-DD'),
    to: today.format('YYYY-MM-DD'),
  };
};

function ColorPalette({ id }) {
  const { top, height, bottom } = useDrawingArea();
  const svgHeight = top + bottom + height;

  return (
    <defs>
      <linearGradient
        id={id}
        x1="0"
        x2="0"
        y1="0"
        y2={`${svgHeight}px`}
        gradientUnits="userSpaceOnUse" // Use the SVG coordinate instead of the component ones.
      >
        <stop offset={0} stopColor="#FFFFFF" stopOpacity={1} />
        <stop offset={0.6} stopColor="#D9DDE14D" stopOpacity={1} />
        <stop offset={1} stopColor="#8291A059" stopOpacity={1} />
      </linearGradient>
    </defs>
  );
}

interface MetalItemProps {
  blockId: string;
  goldBuyPrice: string | number;
  goldSellPrice: string | number;
  silverBuyPrice: string | number;
  silverSellPrice: string | number;
  time: string | number | Date;
}

const ViewDetails = ({
  metalLivePrice,
  metalLivePriceLoading,
  transactionList,
  transactionLoading,
  transactionRefetch,
}) => {
  const [currentHistoricalDataView, setCurrentHistoricalDataView] = useState('ONE_WEEK');
  const [dateRange, setDateRange] = useState(getDateRange(currentHistoricalDataView));
  const [transactionType, setTransactionType] = useState('all');
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [historicalMetalData, setHistoricalMetalData] = useState<any>(null);

  useEffect(() => {
    setDateRange(getDateRange(currentHistoricalDataView));
  }, [currentHistoricalDataView]);

  const { from, to } = dateRange;

  const fetchHistoricalData = useCallback(() => {
    getHistoricalMetalData({ from, to, range: currentHistoricalDataView }).then((response) => {
      setHistoricalMetalData(response);
    });
  }, [currentHistoricalDataView, from, to]);

  useEffect(() => {
    if (metalLivePrice?.data?.expiresAt) {
      const expiresAtTime = new Date(metalLivePrice?.data?.expiresAt).getTime();
      const currentTime = new Date().getTime();
      const timeLeft = expiresAtTime - currentTime;

      if (timeLeft > 0) {
        const timer = setTimeout(() => {
          fetchHistoricalData();
        }, timeLeft);

        return () => clearTimeout(timer);
      }
    }
  }, [fetchHistoricalData, metalLivePrice?.data?.expiresAt]);

  useEffect(() => {
    const transactions = Array.isArray(transactionList?.data) ? transactionList?.data : [];
    const silverTransactions = transactions.filter(
      (transaction) => transaction?.metal?.toUpperCase() === 'SILVER',
    );

    const nextTransactions =
      transactionType?.toUpperCase() === 'BUY'
        ? silverTransactions.filter((transaction) => transaction?.action?.toUpperCase() === 'BUY')
        : transactionType?.toUpperCase() === 'SELL'
        ? silverTransactions.filter((transaction) => transaction?.action?.toUpperCase() === 'SELL')
        : silverTransactions;

    setFilteredTransactions(nextTransactions);
  }, [transactionType, transactionList?.data]);

  useEffect(() => {
    fetchHistoricalData();
  }, [currentHistoricalDataView]);

  useEffect(() => {
    fetchHistoricalData();
    transactionRefetch();
    fetchHistoricalData();
  }, [metalLivePrice?.data?.expiresAt]);

  const handleTransactionTypeChange = (event: SelectChangeEvent<string>) => {
    setTransactionType(event.target.value);
  };

  const changeHistoricalDataView = (val) => {
    setCurrentHistoricalDataView(val);
  };

  const silverPricePercentageChangeRaw = historicalMetalData?.meta?.silverPricePercentageChange;
  const silverPricePercentageChangeNumber =
    typeof silverPricePercentageChangeRaw === 'number'
      ? silverPricePercentageChangeRaw
      : Number(silverPricePercentageChangeRaw);
  const isSilverPricePercentageNegative =
    silverPricePercentageChangeRaw !== undefined &&
    silverPricePercentageChangeRaw !== null &&
    !Number.isNaN(silverPricePercentageChangeNumber) &&
    silverPricePercentageChangeNumber < 0;
  const displaySilverPricePercentageChange =
    silverPricePercentageChangeRaw === undefined || silverPricePercentageChangeRaw === null
      ? undefined
      : !Number.isNaN(silverPricePercentageChangeNumber)
      ? Math.abs(silverPricePercentageChangeNumber)
      : silverPricePercentageChangeRaw;

  return (
    <>
      {/* Description */}
      {/* <div className="border border-amber-700 p-8 rounded-xl bg-[#fbfbf6] relative">

        <div className="absolute top-0 left-0 w-full h-full flex flex-wrap gap-10 overflow-hidden">
          {[...Array(8)].map((_, index) => (
            <div
              key={index}
              className="w-12 h-full rounded-l-xl"
              style={{
                opacity: "0.11",
                background: "linear-gradient(180deg, #D97706 0%, rgba(251, 251, 246, 0.00) 100%)",
                filter: "blur(17.5px)",
                transform: "rotate(-30.369deg)",
              }}
            ></div>
          ))}
        </div>

        <h2 className="font-semibold text-2xl text-amber-700">
          Earn upto 0.5% on every transaction
        </h2>
        <h3 className="text-stone-500 mt-2 text-base">Grow more, withdraw anytime!</h3>
        <div className="bg-stone-200 flex items-center absolute -bottom-6 left-0 rounded-md -z-10 text-stone-500 font-medium text-xs pt-4 px-3 pb-1">
          Powered by Augmont
        </div>
      </div> */}
      <div className="w-full">
        <img src={silverBannerImage} alt="Silver banner" className="w-full h-auto" />
      </div>
      {/* Used from investor */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginTop: '48px',
          // p: '32px',
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Typography
            color={baseColors.stone[500]}
            sx={{ fontSize: '10px !important', fontWeight: 600 }}
          >
            LIVE BUY PRICE
          </Typography>
          {metalLivePrice?.data?.silverBuyPrice ? (
            <Typography
              color={baseColors.stone[700]}
              sx={{
                fontSize: '24px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              {metalLivePriceLoading ? (
                <Skeleton animation="wave" width="100px" />
              ) : (
                getRupeeWithDecimal.format(metalLivePrice?.data?.silverBuyPrice)
              )}
              <Typography
                sx={{
                  color: baseColors.stone[400],
                  fontSize: '12px',
                  fontWeight: 400,
                  marginLeft: '4px',
                }}
              >
                /gm
              </Typography>
            </Typography>
          ) : (
            <Skeleton animation="wave" width="100px" />
          )}
        </Box>
        <Box
          sx={{
            fontSize: '10px',
            fontWeight: 600,
            color: baseColors.stone[400],
            display: 'flex',
            alignItems: 'center',
          }}
        >
          NEXT UPDATE IN
          {metalLivePrice?.data?.expiresAt ? (
            <Typography
              sx={{ color: baseColors.stone[700], fontSize: 'inherit', fontWeight: 'inherit' }}
            >
              <TimerComponent expiresAt={metalLivePrice?.data?.expiresAt} />
            </Typography>
          ) : (
            <Skeleton animation="wave" width="50px" sx={{ ml: 1 }} />
          )}
        </Box>
      </Box>
      {historicalMetalData?.data && (
        <Box>
          {historicalMetalData?.data.length > 5 ? (
            <LineChart
              dataset={historicalMetalData?.data?.sort(
                (a, b) => new Date(a?.time).getTime() - new Date(b?.time).getTime(),
              )}
              xAxis={[
                {
                  scaleType: 'point',
                  data: historicalMetalData?.data?.map(
                    (dataItem: MetalItemProps) => dayjs.utc(dataItem?.time)?.format('DD MMM YYYY'),
                    // dataItem?.time,
                  ),
                },
              ]}
              yAxis={[
                {
                  min: historicalMetalData?.meta?.lowestSilverPrice - 50,
                  max: historicalMetalData?.meta?.highestSilverPrice + 50,
                },
              ]}
              // loading={historicalMetalDataLoading}
              series={[
                {
                  dataKey: 'silverBuyPrice',
                  label: 'Silver',
                  color: '#8393A2',
                  area: true,
                  valueFormatter: (v, { dataIndex }) => {
                    const { silverBuyPrice } = historicalMetalData?.data?.[dataIndex];
                    return getRupeeWithDecimal.format(silverBuyPrice);
                  },
                },
              ]}
              height={300}
              margin={{ left: 5, right: 5, top: 0, bottom: 30 }}
              grid={{ horizontal: true }}
              sx={{
                [`& .${areaElementClasses.root}`]: {
                  fill: 'url(#swich-color-id-2)',
                },
                '& .MuiChartsAxis-directionY': {
                  display: 'none',
                },
                '& .MuiChartsAxis-directionX': {
                  display: 'none',
                },
                '& .MuiChartsLegend-root': {
                  display: 'none',
                },
              }}
            >
              <ColorPalette id="swich-color-id-2" />
            </LineChart>
          ) : (
            <Box display={'flex'} justifyContent={'center'} alignItems={'center'} height={'300px'}>
              <Typography color={baseColors.stone[500]} sx={{ fontSize: '12px', fontWeight: 600 }}>
                Not enough data to display chart
              </Typography>
            </Box>
          )}
          <Tabs
            value={currentHistoricalDataView}
            onChange={(e, value) => {
              changeHistoricalDataView(value);
            }}
            sx={{
              borderRadius: '8px',
              padding: '6px 8px',
              background: 'rgba(0, 0, 0, 0.02)',
              boxShadow: '0px 0px 4px 0px rgba(0, 0, 0, 0.08), 0px 0px 1px 0px rgba(0, 0, 0, 0.12)',
              minHeight: '52px',
              '& .MuiTabs-indicator': {
                backgroundColor: baseColors.white,
                height: '100%',
                borderRadius: '8px',
                zIndex: '-1',
              },
              '& .MuiTabs-flexContainer': {
                justifyContent: 'space-between',
              },

              '& .MuiTab-root': {
                fontSize: '12px',
                fontWeight: 600,
                padding: '6px 12px',
                textTransform: 'none',
                minHeight: '36px',
                borderRadius: '8px',
                margin: '0 4px',
              },
              '& .MuiTab-root.Mui-selected': {
                color: baseColors.stone[900],
                boxShadow:
                  '0px 0px 4px 0px rgba(0, 0, 0, 0.04), 0px 0px 1px 0px rgba(0, 0, 0, 0.12)',
                borderRadius: '8px',
                border: '1px solid #F9F7F5',
              },
            }}
            textColor="inherit"
          >
            <Tab label={'1W'} value={'ONE_WEEK'} />
            <Tab label={'1M'} value={'ONE_MONTH'} />
            <Tab label={'6M'} value={'SIX_MONTHS'} />
            <Tab label={'1Y'} value={'ONE_YEAR'} />
            <Tab label={'5Y'} value={'FIVE_YEARS'} />
          </Tabs>
          <Box
            display={'grid'}
            gridTemplateColumns={'1fr 1fr 1fr'}
            my={'24px'}
            border={'1px solid #D6D3D1'}
            borderRadius={'8px'}
          >
            <Box display={'flex'} flexDirection={'column'} gap={'6px'} padding={'20px 24px'}>
              <Typography color={baseColors.stone[700]} sx={{ fontSize: '12px', fontWeight: 600 }}>
                HIGHEST
              </Typography>
              {historicalMetalData?.meta?.highestSilverPrice === undefined ||
              historicalMetalData?.meta?.highestSilverPrice === null ? (
                <Skeleton animation="wave" width="100px" />
              ) : (
                <Typography
                  color={baseColors.green[800]}
                  display={'flex'}
                  sx={{ fontSize: '18px', fontWeight: 500 }}
                >
                  {getRupeeWithDecimal.format(historicalMetalData?.meta?.highestSilverPrice)}
                </Typography>
              )}
            </Box>
            <Box
              display={'flex'}
              flexDirection={'column'}
              gap={'6px'}
              padding={'20px 24px'}
              borderLeft={'1px solid #D6D3D1'}
              borderRight={'1px solid #D6D3D1'}
            >
              <Typography color={baseColors.stone[700]} sx={{ fontSize: '12px', fontWeight: 600 }}>
                LOWEST
              </Typography>
              {historicalMetalData?.meta?.lowestSilverPrice === undefined ||
              historicalMetalData?.meta?.lowestSilverPrice === null ? (
                <Skeleton animation="wave" width="100px" />
              ) : (
                <Typography
                  color={baseColors.stone[700]}
                  display={'flex'}
                  sx={{ fontSize: '18px', fontWeight: 500 }}
                >
                  {getRupeeWithDecimal.format(historicalMetalData?.meta?.lowestSilverPrice)}
                </Typography>
              )}
            </Box>
            <Box display={'flex'} flexDirection={'column'} gap={'6px'} padding={'20px 24px'}>
              <Typography color={baseColors.stone[700]} sx={{ fontSize: '12px', fontWeight: 600 }}>
                % CHANGE
              </Typography>
              {historicalMetalData?.meta?.silverPricePercentageChange === undefined ||
              historicalMetalData?.meta?.silverPricePercentageChange === null ? (
                <Skeleton animation="wave" width="100px" />
              ) : (
                <Typography
                  color={baseColors.stone[700]}
                  display={'flex'}
                  alignItems={'center'}
                  sx={{ fontSize: '18px', fontWeight: 500 }}
                >
                  {isSilverPricePercentageNegative ? (
                    <ArrowDownwardRounded sx={{ color: baseColors.red[700], fontSize: '18px' }} />
                  ) : (
                    <ArrowUpwardRounded sx={{ color: baseColors.green[700], fontSize: '18px' }} />
                  )}
                  {displaySilverPricePercentageChange} %
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      )}

      <div className="my-8">
        <h3 className="font-semibold text-xl mb-4">Did you know?</h3>
        <div
          className=" flex justify-start items-center gap-2 text-stone-500 text-sm min-h-12 w-fit align-middle px-3 py-4 rounded-xl"
          style={{
            border: '1px solid #8393A2',
            boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.04)',
          }}
        >
          <div>
            <AwardSvg fill="#8393A2" />
          </div>
          <span className="ml-2 text-sm text-stone-500">
            Augmont is the Best Silver Seller in India awarded year on year by Bullion Federation
            Global Convention
          </span>
        </div>
      </div>
      {/* TODO: Instructions need to be updated*/}
      <div>
        <h3 className="font-semibold text-xl mb-6">Silver Investment Payment Instructions</h3>
        <ul className="list-disc flex flex-col gap-2 text-stone-500 ml-4">
          <li>
            <b>Wallet Transactions Not Available:</b> The platform does not support silver
            investments using your existing wallet balance.
          </li>
          <li>
            <b>Payment Link for Investment:</b> A secure payment link will be generated for the
            specified investment amount.
          </li>
          <li>
            <b>Sharing the Link:</b> Share the payment link with your chosen investor or client to
            process the payment.
          </li>
          <li>
            <b>Market Price Adjustment:</b> The investment amount will remain the same, but the
            silver value received may change based on the prevailing market price at the time of
            payment.
          </li>
          <li>
            <b>Investment Success:</b> Once the payment is completed via the link, your silver
            investment will be successfully processed.
          </li>
        </ul>
      </div>
      {/* Transactions */}
      {/* <div className="mt-12">
        <div className="flex items-center">
          <h3 className="font-semibold text-xl ">Your Transactions</h3>
          <div
            className="flex-grow"
            style={{
              backgroundImage: 'linear-gradient(to right, #A8A29E 33%, rgba(255,255,255,0) 0%)',
              backgroundPosition: 'bottom',
              backgroundSize: '15px 1px',
              backgroundRepeat: 'repeat-x',
              height: '1px',
              opacity: '0.8',
            }}
          ></div>
        </div>
        <div className="flex items-center mt-3">
          <h3 className="text-base">For all transaction history sorted by </h3>

          <Select
            value={transactionType}
            onChange={handleTransactionTypeChange}
            className="ml-2 !rounded-full  bg-green-700"
            sx={{
              color: 'white',
              '.MuiSvgIcon-root ': {
                fill: 'white !important',
                fontSize: '14px',
              },
              '& .MuiSelect-select': {
                paddingLeft: 1.2,
                paddingTop: 0.5,
                paddingBottom: 0.5,
                paddingRight: '24px !important',
                border: 'none',
                fontSize: '14px',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  '& .MuiMenuItem-root.Mui-selected': {
                    backgroundColor: `${baseColors.green[700]} !important`,
                    color: baseColors.white,
                  },
                  '& .MuiMenuItem-root.Mui-selected:hover': {
                    backgroundColor: `${baseColors.green[700]} !important`,
                  },
                },
              },
            }}
          >
            <MenuItem value="all" sx={{ fontSize: '14px' }}>
              All
            </MenuItem>
            <MenuItem value="buy" sx={{ fontSize: '14px' }}>
              Buy
            </MenuItem>
            <MenuItem value="sell" sx={{ fontSize: '14px' }}>
              Sell
            </MenuItem>
          </Select>
        </div>
        {transactionLoading ? (
          <div className="flex justify-center items-center px-3 py-4 gap-x-3 w-full  my-3">
            <p className="text-stone-400 text-sm font-medium leading-[21px]">
              Loading <LoadingDots />
            </p>
          </div>
        ) : filteredTransactions?.length > 0 ? (
          filteredTransactions
            ?.slice()
            .map((transaction, index) => <TransactionTile key={index} transaction={transaction} />)
        ) : (
          <div className="flex items-center justify-center h-32">
            <Typography color={baseColors.stone[500]} sx={{ fontSize: '12px', fontWeight: 600 }}>
              No transactions found
            </Typography>
          </div>
        )}
      </div> */}
      {/* Documents */}
      {/* <div className="mt-8">
        <h3 className="font-semibold text-lg mb-6">Documents</h3>
        <div className="flex flex-wrap gap-6">
          {documents &&
            documents.map((document: any) => (
              <a href={document.link} target="_blank" download>
                <div
                  key={document.name}
                  className="border border-stone-200 md:w-96 rounded-lg py-5 px-4 flex justify-between items-center"
                >
                  <div>
                    <h4 className="text-stone-700 font-medium text-sm">{document.name}</h4>
                    <p className="text-stone-500 text-sm">{document.text}</p>
                  </div>
                  <DownloadForOfflineOutlinedIcon className="text-stone-500" />
                </div>
              </a>
            ))}
        </div>
      </div> */}
    </>
  );
};

export default ViewDetails;
